import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TraderModal from './TraderModal';
import DeletionReasonModal from './DeletionReasonModal';
import * as XLSX from 'xlsx';
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaUser,
  FaPhone,
  FaStore,
  FaMapMarkerAlt,
  FaDownload,
  FaUpload,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';

const TradersView = ({ userInfo, API_BASE_URL, auth, onViewChange }) => {
  const [traders, setTraders] = useState([]);
  const [filteredTraders, setFilteredTraders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTrader, setSelectedTrader] = useState(null);
  const [viewTrader, setViewTrader] = useState(null);
  const [selectedTraderForDeletion, setSelectedTraderForDeletion] = useState(null);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    type: '', // 'success' or 'error'
    title: '',
    message: ''
  });

  // Detect screen width for responsive behavior
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchTraders();
  }, []);

  useEffect(() => {
    filterTraders();
  }, [traders, searchTerm]);

  const fetchTraders = async () => {
    try {
      setLoading(true);

      // Validate zone assignment
      if (!userInfo.zone_id) {
        console.warn('No zone assigned to field officer');
        setTraders([]);
        setLoading(false);
        return;
      }

      console.log('Fetching traders for zone:', userInfo.zone_id, 'MMDA:', userInfo.mmda_id);

      // Use the search endpoint with zone filtering
      const response = await axios.get(
        `${API_BASE_URL}/traders/search?userRole=Revenue Officer&mmda_id=${userInfo.mmda_id}&zone_id=${userInfo.zone_id}&name=`,
        auth
      );

      // If search endpoint doesn't work, fallback to regular endpoint with manual filtering
      if (response.data.length === 0) {
        const fallbackResponse = await axios.get(
          `${API_BASE_URL}/traders?userRole=Revenue Officer&mmda_id=${userInfo.mmda_id}`,
          auth
        );
        // Filter by zone_id on frontend if backend doesn't filter properly
        const filteredTraders = fallbackResponse.data.filter(trader =>
          trader.zone_id === userInfo.zone_id || trader.zone_id === parseInt(userInfo.zone_id)
        );
        setTraders(filteredTraders);
      } else {
        setTraders(response.data);
      }
    } catch (error) {
      console.error('Error fetching traders:', error);
      // Try fallback approach
      try {
        const fallbackResponse = await axios.get(`${API_BASE_URL}/traders`, auth);
        const filteredTraders = fallbackResponse.data.filter(trader =>
          trader.zone_id === userInfo.zone_id || trader.zone_id === parseInt(userInfo.zone_id)
        );
        setTraders(filteredTraders);
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
        setTraders([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const filterTraders = () => {
    if (!searchTerm.trim()) {
      setFilteredTraders(traders);
    } else {
      const filtered = traders.filter(trader =>
        trader.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trader.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trader.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trader.phone?.includes(searchTerm)
      );
      setFilteredTraders(filtered);
    }
    setCurrentPage(1);
  };

  // Excel Download Function
  const downloadTradersExcel = () => {
    try {
      // Prepare data for Excel
      const excelData = filteredTraders.map(trader => ({
        'Business Name': trader.business_name || '',
        'Owner Name': trader.owner_name || `${trader.first_name || ''} ${trader.last_name || ''}`.trim(),
        'First Name': trader.first_name || '',
        'Last Name': trader.last_name || '',
        'Phone': trader.phone || '',
        'Email': trader.email || '',
        'Business Type': trader.business_type || 'General',
        'Zone': trader.zone_name || 'Not Assigned',
        'Location': trader.location || '',
        'Registration Date': trader.registration_date ? new Date(trader.registration_date).toLocaleDateString() :
                            trader.created_at ? new Date(trader.created_at).toLocaleDateString() : '',
        'Status': trader.status || 'Active',
        'MMDA': trader.mmda_name || '',
        'Created Date': trader.created_at ? new Date(trader.created_at).toLocaleDateString() : ''
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const columnWidths = [
        { wch: 25 }, // Business Name
        { wch: 20 }, // Owner Name
        { wch: 15 }, // First Name
        { wch: 15 }, // Last Name
        { wch: 15 }, // Phone
        { wch: 25 }, // Email
        { wch: 15 }, // Business Type
        { wch: 20 }, // Zone
        { wch: 25 }, // Location
        { wch: 15 }, // Registration Date
        { wch: 10 }, // Status
        { wch: 20 }, // MMDA
        { wch: 15 }, // Created Date
      ];
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Traders');

      // Generate filename with current date
      const filename = `traders_export_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);

      // Show success notification
      setNotification({
        show: true,
        type: 'success',
        title: 'Export Successful',
        message: `Traders exported successfully! Downloaded as ${filename}`
      });
    } catch (error) {
      console.error('Error exporting traders to Excel:', error);

      // Show error notification
      setNotification({
        show: true,
        type: 'error',
        title: 'Export Failed',
        message: 'Error exporting traders. Please try again.'
      });
    }
  };

  const handleNotificationClose = () => {
    setNotification({
      show: false,
      type: '',
      title: '',
      message: ''
    });
  };

  const requestTraderDeletion = async (trader) => {
    setSelectedTraderForDeletion(trader);
    setShowDeletionModal(true);
  };

  const handleDeletionSubmit = async (reason) => {
    if (!selectedTraderForDeletion || !reason.trim()) return;

    try {
      // Create support ticket for deletion request
      const ticketData = {
        title: `Delete Request - Trader: ${selectedTraderForDeletion.business_name || selectedTraderForDeletion.owner_name}`,
        description: `Field Officer ${userInfo.full_name} is requesting deletion of trader:\n\nTrader Details:\n- Business Name: ${selectedTraderForDeletion.business_name || 'N/A'}\n- Owner Name: ${selectedTraderForDeletion.owner_name || 'N/A'}\n- Phone: ${selectedTraderForDeletion.phone || 'N/A'}\n- Location: ${selectedTraderForDeletion.location || 'N/A'}\n- Zone: ${userInfo.zone_name || 'N/A'}\n\nReason for Deletion:\n${reason}\n\nTrader ID: ${selectedTraderForDeletion.trader_id}`,
        priority: 'medium',
        category: 'data_correction',
        created_by: userInfo.user_id
      };

      await axios.post(
        `${API_BASE_URL}/support-tickets`,
        ticketData,
        auth
      );

      // Show success notification
      setNotification({
        show: true,
        type: 'success',
        title: 'Deletion Request Submitted',
        message: `Deletion request for trader "${selectedTraderForDeletion.business_name || selectedTraderForDeletion.owner_name}" has been submitted as a support ticket to your MMDA. It will be reviewed by the back office.`
      });

      // Close modal and reset
      setShowDeletionModal(false);
      setSelectedTraderForDeletion(null);
    } catch (error) {
      console.error('Error submitting deletion request:', error);

      // Show error notification
      setNotification({
        show: true,
        type: 'error',
        title: 'Failed to Submit Deletion Request',
        message: error.response?.data?.error || 'Failed to submit deletion request. Please try again.'
      });
    }
  };

  const handleAddTrader = () => {
    setSelectedTrader(null);
    setIsModalOpen(true);
  };

  const handleEditTrader = (trader) => {
    setSelectedTrader(trader);
    setIsModalOpen(true);
  };

  const handleViewTrader = (trader) => {
    setViewTrader(trader);
  };

  const handleModalSave = () => {
    fetchTraders(); // Refresh the list
    // Don't close modal immediately - let notification show first
    setTimeout(() => {
      setIsModalOpen(false);
      setSelectedTrader(null);
    }, 100);
  };

  const handleModalNotification = (notificationData) => {
    setNotification(notificationData);
    // If it's a success notification, close the modal after a delay
    if (notificationData.type === 'success') {
      setTimeout(() => {
        setIsModalOpen(false);
        setSelectedTrader(null);
      }, 2000); // Give user time to see the success message
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchTraders();
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/traders/search?${searchTerm.includes('@') || searchTerm.length > 10 ? 'phone' : 'name'}=${searchTerm}&userRole=Revenue Officer&mmda_id=${userInfo.mmda_id}&zone_id=${userInfo.zone_id}`,
        auth
      );
      setFilteredTraders(response.data);
    } catch (error) {
      console.error('Error searching traders:', error);
      setFilteredTraders([]);
    } finally {
      setLoading(false);
    }
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTraders = filteredTraders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTraders.length / itemsPerPage);

  const TraderCard = ({ trader }) => (
    <div style={{
      backgroundColor: 'white',
      padding: '1.5rem',
      borderRadius: '0.5rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      border: '1px solid #e9ecef'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ 
            margin: '0 0 0.5rem 0', 
            color: '#2c3e50',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <FaUser color="#3498db" />
            {trader.first_name} {trader.last_name}
          </h3>
          
          <div style={{ marginBottom: '0.5rem' }}>
            <span style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              color: '#7f8c8d',
              fontSize: '0.9rem'
            }}>
              <FaPhone size={14} />
              {trader.phone}
            </span>
          </div>

          {trader.business_name && (
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                color: '#7f8c8d',
                fontSize: '0.9rem'
              }}>
                <FaStore size={14} />
                {trader.business_name}
              </span>
            </div>
          )}

          <div style={{ marginBottom: '0.5rem' }}>
            <span style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              color: '#7f8c8d',
              fontSize: '0.9rem'
            }}>
              <FaMapMarkerAlt size={14} />
              {trader.zone_name} - {trader.group_name}
            </span>
          </div>

          <div style={{ marginTop: '1rem' }}>
            <span style={{
              padding: '0.25rem 0.5rem',
              borderRadius: '0.25rem',
              fontSize: '0.75rem',
              fontWeight: '500',
              backgroundColor: trader.status === 'active' ? '#d4edda' : '#f8d7da',
              color: trader.status === 'active' ? '#155724' : '#721c24'
            }}>
              {trader.status === 'active' ? 'Active' : 'Inactive'}
            </span>
            <span style={{
              marginLeft: '0.5rem',
              fontSize: '0.75rem',
              color: '#95a5a6'
            }}>
              ID: {trader.trader_id}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <button
            onClick={() => handleViewTrader(trader)}
            style={{
              padding: '0.5rem',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="View Details"
          >
            <FaEye size={14} />
          </button>

          <button
            onClick={() => handleEditTrader(trader)}
            style={{
              padding: '0.5rem',
              backgroundColor: '#f39c12',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Edit Trader"
          >
            <FaEdit size={14} />
          </button>

          <button
            onClick={() => requestTraderDeletion(trader)}
            style={{
              padding: '0.5rem',
              backgroundColor: '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Request Deletion"
          >
            <FaTrash size={14} />
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div>Loading traders...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '1rem' : '0',
        marginBottom: '2rem'
      }}>
        <h1 style={{ margin: 0, color: '#2c3e50', textAlign: isMobile ? 'center' : 'left' }}>
          Traders in Your Zone
        </h1>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '1rem', width: isMobile ? '100%' : 'auto' }}>
          <button
            onClick={downloadTradersExcel}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            <FaDownload />
            Download Excel
          </button>
          <button
            onClick={() => onViewChange('Upload Traders')}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#f39c12',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            <FaUpload />
            Upload Traders
          </button>
          <button
            onClick={handleAddTrader}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            <FaPlus />
            Add New Trader
          </button>
        </div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '1rem', maxWidth: isMobile ? '100%' : '500px' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <FaSearch
              style={{
                position: 'absolute',
                left: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#7f8c8d'
              }}
            />
            <input
              type="text"
              placeholder="Search by phone number or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 2.5rem',
                border: '1px solid #ddd',
                borderRadius: '0.5rem',
                fontSize: '1rem'
              }}
            />
          </div>
          <button
            onClick={handleSearch}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <FaSearch />
            Search
          </button>
          {searchTerm && (
            <button
              onClick={() => {
                setSearchTerm('');
                fetchTraders();
              }}
              style={{
                padding: '0.75rem 1rem',
                backgroundColor: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(auto-fit, minmax(150px, 1fr))' : 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1rem',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#3498db' }}>
            {filteredTraders.length}
          </h3>
          <p style={{ margin: 0, color: '#7f8c8d' }}>Total Traders</p>
        </div>
        
        <div style={{
          backgroundColor: 'white',
          padding: '1rem',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#27ae60' }}>
            {filteredTraders.filter(t => t.status === 'active').length}
          </h3>
          <p style={{ margin: 0, color: '#7f8c8d' }}>Active Traders</p>
        </div>
      </div>

      {/* Traders Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {currentTraders.map((trader) => (
          <TraderCard key={trader.trader_id} trader={trader} />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '0.5rem',
          marginBottom: '2rem'
        }}>
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: currentPage === 1 ? '#95a5a6' : '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <FaChevronLeft />
            Previous
          </button>

          <span style={{
            padding: '0 1rem',
            color: '#7f8c8d',
            fontSize: '0.9rem',
            fontWeight: '500'
          }}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: currentPage === totalPages ? '#95a5a6' : '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            Next
            <FaChevronRight />
          </button>
        </div>
      )}

      {filteredTraders.length === 0 && !loading && (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: '#7f8c8d'
        }}>
          <FaUser size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <h3>No traders found</h3>
          <p>No traders match your search criteria or you don't have any traders in your zone yet.</p>
        </div>
      )}

      {/* Add/Edit Trader Modal */}
      <TraderModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTrader(null);
        }}
        trader={selectedTrader}
        userInfo={userInfo}
        API_BASE_URL={API_BASE_URL}
        auth={auth}
        onSave={handleModalSave}
        onNotification={handleModalNotification}
      />

      {/* View Trader Modal */}
      {viewTrader && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            padding: isMobile ? '1rem' : '2rem',
            width: '90%',
            maxWidth: isMobile ? '95%' : '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{ margin: 0, color: '#2c3e50' }}>Trader Details</h2>
              <button
                onClick={() => setViewTrader(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#7f8c8d'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div><strong>Name:</strong> {viewTrader.first_name} {viewTrader.middle_name} {viewTrader.last_name}</div>
              <div><strong>Phone:</strong> {viewTrader.phone}</div>
              <div><strong>Business:</strong> {viewTrader.business_name || 'N/A'}</div>
              <div><strong>Zone:</strong> {viewTrader.zone_name}</div>
              <div><strong>Group:</strong> {viewTrader.group_name || 'N/A'}</div>
              <div><strong>National ID:</strong> {viewTrader.national_info || 'N/A'}</div>
              <div><strong>Status:</strong>
                <span style={{
                  marginLeft: '0.5rem',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem',
                  fontSize: '0.8rem',
                  backgroundColor: viewTrader.status === 'active' ? '#d4edda' : '#f8d7da',
                  color: viewTrader.status === 'active' ? '#155724' : '#721c24'
                }}>
                  {viewTrader.status}
                </span>
              </div>
              <div><strong>Trader ID:</strong> {viewTrader.trader_id}</div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {notification.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            textAlign: 'center'
          }}>
            {/* Icon */}
            <div style={{
              marginBottom: '1rem',
              fontSize: '3rem'
            }}>
              {notification.type === 'success' ? '✅' : '❌'}
            </div>

            {/* Title */}
            <h3 style={{
              margin: '0 0 1rem 0',
              color: notification.type === 'success' ? '#27ae60' : '#e74c3c',
              fontSize: '1.25rem',
              fontWeight: 'bold'
            }}>
              {notification.title}
            </h3>

            {/* Message */}
            <p style={{
              margin: '0 0 2rem 0',
              color: '#666',
              fontSize: '1rem',
              lineHeight: '1.5'
            }}>
              {notification.message}
            </p>

            {/* Close Button */}
            <button
              onClick={handleNotificationClose}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: notification.type === 'success' ? '#27ae60' : '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                minWidth: '100px'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Deletion Reason Modal */}
      <DeletionReasonModal
        isOpen={showDeletionModal}
        onClose={() => {
          setShowDeletionModal(false);
          setSelectedTraderForDeletion(null);
        }}
        onSubmit={handleDeletionSubmit}
        itemType="trader"
        itemName={selectedTraderForDeletion ?
          (selectedTraderForDeletion.business_name || selectedTraderForDeletion.owner_name) :
          ''
        }
        loading={false}
      />
    </div>
  );
};

export default TradersView;
